import { navigate, RouteComponentProps } from '@reach/router';
import { AppPath } from 'appConstants';
import Pointer from 'assets/new_skin/pointer.svg';
import { Button } from 'components/button/Button';
import { getQueryParam } from 'helpers/getQueryParam';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAuthStatus,
  selectWebskin,
  setApplicationLinkToken,
  setInvitedBy,
  setInviteType,
  setIsAssessmentOnlyCandidate,
  selectHomeLoadingScreen,
} from 'reducers/auth';
import {
  selectSignInFormState,
  setOpenSignInForm,
  setOpenSignUpForm,
} from 'reducers/landingPage';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import { addHubspotTracking } from 'helpers/addHubspotTracking';
import ClevryLandingImage from 'assets/new_skin/Img_NewFindYourJoy_LandingPage_03.png';
import { Spinner } from 'components/spinner/Spinner';
import { SelectOption } from 'components/dropdown/model';
import { selectLandingPageIntroText } from 'reducers/landingPage';

export enum FormType {
  SIGN_IN = 1,
  SIGN_UP,
}

const Landing: React.FC<RouteComponentProps> = () => {
  const { t } = useTranslation('Landing', { i18n });

  const dispatch = useDispatch();
  const signInFormState = useSelector(selectSignInFormState);
  const authStatus = useSelector(selectAuthStatus);
  const selectedWebskin = useSelector(selectWebskin);
  const landingPageIntroText = useSelector(selectLandingPageIntroText);
  const loadHomeScreen = useSelector(selectHomeLoadingScreen);

  const currentWebskin = window.sessionStorage.getItem('webskin')
    ? window.sessionStorage.getItem('webskin')
    : 'defaultTheme';

  const mainLandingImage =
    selectedWebskin && selectedWebskin.siteImages
      ? selectedWebskin.siteImages.find(
          (item: any) => item.code === 'SI_Landing_Main'
        ).imageDownloadURI
      : '';

  useEffect(() => {
    const inviteType = getQueryParam('inviteType');
    if (inviteType) {
      dispatch(setInviteType(inviteType));
    }
    const script = addHubspotTracking();

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // useEffect(() => {
  //   const applicationLinkToken = getQueryParam('jobtoken');
  //   if (applicationLinkToken) {
  //     dispatch(setApplicationLinkToken(applicationLinkToken));

  //     if (currentWebskin !== 'defaultTheme') {
  //       navigate(AppPath.SIGN_UP_PAGE);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    const invitedById = getQueryParam('invitedBy');
    if (invitedById) {
      dispatch(setInvitedBy(invitedById));
    }
  }, []);

  useEffect(() => {
    dispatch(setIsAssessmentOnlyCandidate(false));
    localStorage.removeItem('assessmentOnlyCandidate');
  }, []);

  if (loadHomeScreen) {
    return (
      <div className="PageSpinner FullScreen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="LandingPage">
      {currentWebskin === 'defaultTheme' ? (
        <div className="SignIn WidthContent HeightContent">
          <div className="Content Body">
            {landingPageIntroText ?
              <div
                className="LandingPageIntroCustomText"
                dangerouslySetInnerHTML={{ __html: landingPageIntroText }}
              ></div>
            :
              <>
                <h1 className="Title">
                  {t('SSCandidate_Landing_Sign_In_Title_Restructure')}
                </h1>
                <h2 className="Subtitle">
                  {t('SSCandidate_Landing_Sign_In_Subtitle_Restructure_1')}
                </h2>
              </>
            }
            <div className="ButtonContainer">
              <Button
                priority="primary"
                isPublic
                className=""
                onClick={() =>
                  authStatus === 'signed_in'
                    ? navigate(AppPath.BROWSE_OPPORTUNITIES)
                    : navigate(AppPath.PUBLIC_BROWSE_OPPORTUNITIES)
                }
              >
                {t('SSCandidate_Landing_Sign_In_Open_Vacancies')}
              </Button>
            </div>
            <h2 className="Subtitle">
              {t('SSCandidate_Landing_Sign_In_Subtitle_Restructure_2')}
            </h2>
            <div className="ButtonContainer">
              {/* <Button
                priority="primary"
                isPublic
                className="LandingButton"
                onClick={() => navigate(AppPath.FIND_YOUR_JOY)}
              >
                {t('SSCandidate_Landing_Sign_In_Joy_At_Work_Button')}
              </Button>
              <img src={Pointer} className="Pointer" /> */}
              <Button
                priority="green"
                isPublic
                className=""
                onClick={() =>
                  authStatus === 'signed_in'
                    ? navigate(AppPath.MATCHING_PROFILE)
                    : dispatch(setOpenSignUpForm(AppPath.MATCHING_PROFILE))
                }
              >
                {t('SSCandidate_Landing_Sign_In_Create_Profile')}
              </Button>
              <Button
                priority="green"
                isPublic
                className=""
                onClick={() =>
                  authStatus === 'signed_in'
                    ? navigate(AppPath.MY_DEVELOPMENT)
                    : dispatch(setOpenSignUpForm(AppPath.MY_DEVELOPMENT))
                }
              >
                {t('SSCandidate_Landing_Sign_In_Practice_Tests')}
              </Button>
            </div>
          </div>
          <div className="LandingImageContainer">
            <img
              src={ClevryLandingImage}
              alt={t('SSCandidate_Landing_Alt_Find_Your_Joy')}
              className="LandingImage"
            />
          </div>
        </div>
      ) : (
        <div className="SignIn Webskin WidthContent">
          <div className="Content Top">
            <div className="Content Body">
              {landingPageIntroText ?
                <div
                  className="LandingPageIntroCustomText"
                  dangerouslySetInnerHTML={{ __html: landingPageIntroText }}
                ></div>
              :
                <>
                  <h1 className="Title" style={getStyle('ST_H1', selectedWebskin)}>
                    Welcome to your {currentWebskin} Assessment
                  </h1>
                  <h2
                    className="Subtitle"
                    style={getStyle('ST_P', selectedWebskin)}
                  >
                    Sign up to take an assessment
                  </h2>
                </>
              }              

              <Button
                priority="secondary"
                isPublic
                onClick={() => navigate(AppPath.SIGN_UP_PAGE)}
                style={getStyle('ST_P', selectedWebskin)}
                // disabled={true}
              >
                Sign up
              </Button>

              <div className="SignInPrompt">
                <h2
                  className="Subtitle"
                  style={getStyle('ST_P', selectedWebskin)}
                >
                  Already signed-up?
                </h2>
                <h2
                  className="Subtitle__Link"
                  onClick={() => {
                    dispatch(setOpenSignInForm());
                  }}
                  style={getStyle('ST_P', selectedWebskin)}
                >
                  Sign in
                </h2>
              </div>
            </div>
            <img
              src={mainLandingImage}
              // src={themes[`${currentWebskin}`][`landingImage`]}
              alt={t('SSCandidate_Landing_Alt_Find_Your_Joy')}
              className="LandingImage"
            />
          </div>
          {/* <div className="Content Bottom">
            <div className="Content Body">
              <h1 className="Title">Taking a test</h1>
              <h2 className="Subtitle">Sign up to take an assessment</h2>

              <Button
                priority="secondary"
                isPublic
                onClick={() => {}}
                disabled={true}
              >
                See more
              </Button>
            </div>
            <div className="AssessmentBlockList">
              <div className="AssessmentBlockItem">
                <img
                  src={landingBlockImage1}
                  // src={themes[`${currentWebskin}`][`landingBlockImage1`]}
                  alt="AssessmentBlockImage"
                />
                <span>Advice for taking tests</span>
              </div>
              <div className="AssessmentBlockItem">
                <img src={landingBlockImage2} alt="AssessmentBlockImage" />
                <span>Practice for a numerical test</span>
              </div>
              <div className="AssessmentBlockItem">
                <img src={landingBlockImage3} alt="AssessmentBlockImage" />
                <span>Practice for a verbal test</span>
              </div>
            </div>
          </div> */}
          <div
            className="Content Bottom"
            style={{ width: '70vw', padding: '0px', marginTop: '-50px' }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default Landing;

type WebskinDetails = {
  skinName: string;
  siteColours: {
    code: string;
    colourValue: string;
  }[];
  textColours: {
    code: string;
    colourValue: string;
  }[];
  siteTypography: [
    {
      code: 'ST_H1';
      fontValueId: 1000;
      fontSizeValue: 11;
    },
    {
      code: 'ST_P';
      fontValueId: 1000;
      fontSizeValue: 11;
    },
    {
      code: 'ST_TableData';
      fontValueId: 1000;
      fontSizeValue: 11;
    }
  ];
  siteImages: {
    code: string;
    imageCaption: string | null;
    imageAltText: string | null;
    imageDownloadURI: string | null;
  }[];
  customTexts: {
    code: string;
    customText: string | null;
  }[];
  availableFonts: SelectOption[];
};

const getStyle = (
  code: string,
  webskin: WebskinDetails | null
): React.CSSProperties => {
  if (!webskin) return {};
  const selectedTypography = webskin.siteTypography?.find(
    (item) => item.code === code
  );
  if (!selectedTypography) return {};
  return {
    fontSize: selectedTypography?.fontSizeValue ?? undefined,
    fontFamily: mapFontName(
      webskin.availableFonts.find(
        (option) => option.value === selectedTypography.fontValueId
      )?.label
    ),
  };
};

const mapFontName = (fontName: string | undefined): string | undefined => {
  if (!fontName) return undefined;
  if (fontName === 'M&S London') return 'MandSLondon-Regular';
  return fontName;
};
